import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  createCoreStatus,
  deleteCoreStatus,
  listCoreStatuses,
  STATUS_COLORS,
  STATUS_SCOPES,
  statusColorClass,
  statusScopeMeta,
  updateCoreStatus,
  type CoreStatus,
} from '@/lib/coreSystem';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

interface FormState { id: number | null; scope: string; code: string; label: string; color: string; }
const EMPTY: FormState = { id: null, scope: 'generic', code: '', label: '', color: 'secondary' };

export default function StatusesTab({ org }: Props) {
  const [statuses, setStatuses] = useState<CoreStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scope, setScope] = useState('generic');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setStatuses(await listCoreStatuses(org.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const list = useMemo(() => statuses.filter((s) => s.scope === scope), [statuses, scope]);

  const openCreate = () => { setForm({ ...EMPTY, scope }); setOpen(true); };
  const openEdit = (s: CoreStatus) => { setForm({ id: s.id, scope: s.scope, code: s.code, label: s.label, color: s.color || 'secondary' }); setOpen(true); };

  const save = async () => {
    if (!form.code.trim() || !form.label.trim()) return;
    setSaving(true);
    try {
      if (form.id) {
        await updateCoreStatus(form.id, { label: form.label.trim(), color: form.color, scope: form.scope, code: form.code.trim() });
      } else {
        await createCoreStatus({
          tenantId: org.tenant_id, organizationId: org.id, scope: form.scope,
          code: form.code.trim().toLowerCase().replace(/\s+/g, '_'), label: form.label.trim(), color: form.color,
        });
      }
      setOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s: CoreStatus) => {
    setBusyId(s.id);
    try { await deleteCoreStatus(s.id); load(); } finally { setBusyId(null); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <p className="text-xs text-foreground-500 max-w-2xl">
          Statuts standard partagés (actif, brouillon, confirmé…). Chaque module peut aussi définir ses propres statuts métier —
          le socle n'impose <span className="font-semibold text-foreground-700">aucun workflow universel</span>.
        </p>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
          <i className="ri-add-line"></i> Statut métier
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-1 bg-background-100 rounded-full p-1 mb-4 w-fit">
        {STATUS_SCOPES.map((s) => (
          <button key={s.key} onClick={() => setScope(s.key)} className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${scope === s.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
            <i className={s.icon}></i>{s.label}
          </button>
        ))}
      </div>

      {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : list.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-shield-check-line text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Aucun statut pour « {statusScopeMeta(scope).label} ».</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {list.map((s) => (
            <div key={s.id} className="flex items-center gap-3 bg-background-50 border border-background-200/70 rounded-lg p-3">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium whitespace-nowrap ${statusColorClass(s.color)}`}>
                <i className={s.is_system ? 'ri-lock-line' : 'ri-price-tag-3-line'}></i>{s.label}
              </span>
              <code className="text-[11px] text-foreground-400 truncate">{s.code}</code>
              <div className="ml-auto flex items-center gap-1">
                {s.is_system ? (
                  <span className="text-[10px] text-foreground-400 px-2 whitespace-nowrap">Standard</span>
                ) : (
                  <>
                    <button onClick={() => openEdit(s)} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"><i className="ri-edit-line"></i></button>
                    <button onClick={() => remove(s)} disabled={busyId === s.id} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-delete-bin-line"></i></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <Modal
          title={form.id ? 'Modifier le statut' : 'Nouveau statut métier'}
          onClose={() => setOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={save} disabled={saving || !form.code.trim() || !form.label.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Enregistrer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Portée (module)</span>
              <select className={INPUT} value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
                {STATUS_SCOPES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Code</span>
                <input className={INPUT} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Ex : refunded" />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Libellé</span>
                <input className={INPUT} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ex : Remboursé" />
              </label>
            </div>
            <div>
              <span className="block text-xs font-medium text-foreground-600 mb-1">Couleur</span>
              <div className="flex flex-wrap gap-2">
                {STATUS_COLORS.map((c) => (
                  <button key={c.key} type="button" onClick={() => setForm({ ...form, color: c.key })} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors cursor-pointer ${form.color === c.key ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-background-200/70 text-foreground-600 hover:bg-background-100'}`}>
                    <span className={`w-3 h-3 rounded-full border ${c.className}`}></span>{c.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}